import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageNote from "@/components/PageNote";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Target, TrendingUp, Building2, User, Mail, Loader2, Globe, Megaphone, Settings, ExternalLink, AlertCircle } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";

// Nav menu dark color for chart text
const NAV_DARK_COLOR = "#0F172A";

// Helper function to format week labels from "2025-W01" to "Jan 6"
const formatWeekLabel = (weekStr) => {
  if (!weekStr || !weekStr.includes("-W")) return weekStr;
  
  try {
    // Parse "2025-W01" format
    const [year, weekPart] = weekStr.split("-W");
    const weekNum = parseInt(weekPart, 10);
    
    // Calculate the date of the Monday of that week
    // Jan 4th is always in week 1, so we use that as reference
    const jan4 = new Date(parseInt(year, 10), 0, 4);
    const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);
    
    // Add weeks to get to the target week
    const targetDate = new Date(mondayOfWeek1);
    targetDate.setDate(mondayOfWeek1.getDate() + (weekNum - 1) * 7);
    
    // Format as "Jan 6"
    return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) {
    return weekStr;
  }
};

const MQLsPage = () => {
  const { sessionToken, user } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskName, maskEmail, maskValue, maskNumber, maskSource } = usePresentationMode();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [stageMapping, setStageMapping] = useState({});
  const [configuredMqlStages, setConfiguredMqlStages] = useState([]);
  const [regionalData, setRegionalData] = useState({ na: null, emea: null });
  const [data, setData] = useState({
    total: 0,
    mqls: [],
    trend: [],
    by_source: [],
    stage_breakdown: []
  });

  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== MQLsPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  const handleDateChange = useCallback((dateInfo) => {
    console.log("MQLs date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  // Fetch stage mapping for label translation
  const fetchStageMapping = async () => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/integrations/hubspot/lifecycle-stages`, { params });
      const mapping = response.data?.stage_mapping || {};
      setStageMapping(mapping);
    } catch (error) {
      console.error("Error fetching stage mapping:", error);
    }
  };

  // Get stage label from ID
  const getStageLabel = (stageId) => {
    if (!stageId) return "Unknown";
    const stageStr = String(stageId);
    // Check if we have a mapping for this ID (string match)
    if (stageMapping[stageStr]) return stageMapping[stageStr];
    // Check lowercase version
    if (stageMapping[stageStr.toLowerCase()]) return stageMapping[stageStr.toLowerCase()];
    // Check if it's already a readable label (not just numbers)
    if (isNaN(stageStr) && stageStr.length > 0) {
      // Capitalize first letter
      return stageStr.charAt(0).toUpperCase() + stageStr.slice(1).replace(/_/g, ' ');
    }
    // Return the ID if no mapping found
    return stageStr;
  };

  const fetchMQLs = async () => {
    setLoading(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      if (selectedRegion && selectedRegion !== "all") params.region = selectedRegion;
      if (selectedSource && selectedSource !== "all") params.source = selectedSource;

      console.log("=== MQLsPage fetchMQLs ===", "tenantId:", viewingTenantId);
      const response = await axios.get(`${API}/analytics/mqls`, { params });
      setData(response.data);
      
      // Update stage mapping if backend provides one (ID -> label)
      if (response.data.stage_mapping) {
        setStageMapping(response.data.stage_mapping);
      }
      
      // Fetch regional comparison data (NA vs EMEA)
      const baseParams = { ...params };
      delete baseParams.region; // Remove region filter for regional comparison
      
      const [naResponse, emeaResponse] = await Promise.all([
        axios.get(`${API}/analytics/mqls`, { params: { ...baseParams, region: 'na' } }),
        axios.get(`${API}/analytics/mqls`, { params: { ...baseParams, region: 'emea' } })
      ]);
      
      setRegionalData({
        na: naResponse.data,
        emea: emeaResponse.data
      });
    } catch (error) {
      console.error("=== MQL FETCH ERROR ===");
      console.error("Error message:", error.message);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Full error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch configured MQL stages from tenant settings
  const fetchConfiguredStages = async () => {
    const viewingTenantId = currentTenantId || sessionStorage.getItem("viewing_tenant_id");
    // Don't fetch if no tenant ID is available
    if (!viewingTenantId) {
      console.log("No tenant ID available yet, skipping stage fetch");
      return;
    }
    
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/tenants/my-tenant-settings`, { params });
      
      const stages = response.data?.hubspot_stage_mapping?.mql_stages || [];
      console.log("Fetched MQL stages from tenant settings:", stages, "for tenant:", viewingTenantId);
      setConfiguredMqlStages(stages);
    } catch (error) {
      console.error("Error fetching configured stages:", error);
    }
  };

  // Check if current user is an admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || 
    user?.email?.endsWith('@jci-marketing.com') || 
    ['jason@jci-marketing.com', 'kevin@jci-marketing.com'].includes(user?.email);

  useEffect(() => {
    if (sessionToken && currentTenantId) {
      fetchStageMapping();
      fetchConfiguredStages();
    }
  }, [sessionToken, currentTenantId]); // CRITICAL: Re-fetch when tenant changes
  
  // Also listen for tenant-changed event to re-fetch stages immediately
  useEffect(() => {
    const handleTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId) {
        console.log("tenant-changed event received, re-fetching stages for:", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    window.addEventListener("tenant-changed", handleTenantChange);
    return () => window.removeEventListener("tenant-changed", handleTenantChange);
  }, []);

  useEffect(() => {
    if (!dateFilterInitialized) return;
    // Just fetch data from local database - no sync on page load
    fetchMQLs();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, selectedRegion, selectedSource, currentTenantId]); // CRITICAL: Added currentTenantId

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="mqls-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-600" />
            {pageTitles?.mqls || "Marketing Qualified Leads"}
          </h1>
          <p className="text-slate-600 mt-1">
            Contacts identified as potential customers based on engagement
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger className="w-40">
              <Megaphone className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="PAID_SEARCH">Paid Search</SelectItem>
              <SelectItem value="ORGANIC_SEARCH">Organic</SelectItem>
              <SelectItem value="DIRECT_TRAFFIC">Direct</SelectItem>
              <SelectItem value="REFERRALS">Referral</SelectItem>
              <SelectItem value="SOCIAL_MEDIA">Social</SelectItem>
              <SelectItem value="EMAIL_MARKETING">Email</SelectItem>
              <SelectItem value="PAID_SOCIAL">Paid Social</SelectItem>
              <SelectItem value="OFFLINE">Offline</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-36">
              <Globe className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="na">NA</SelectItem>
              <SelectItem value="emea">EMEA</SelectItem>
              <SelectItem value="apac">APAC</SelectItem>
            </SelectContent>
          </Select>
          <PageDateFilter pageId="mqls" onDateChange={handleDateChange} />
        </div>
      </div>

      {/* Page Notes & Market Insights */}
      <PageNote pageId="mql_sql" />

      {/* Mapped Stages Indicator */}
      <Card 
        className={`border-purple-200 bg-purple-50/50 ${isAdmin ? 'cursor-pointer hover:bg-purple-100 transition-colors' : ''}`}
        onClick={isAdmin ? () => navigate('/app/admin?tab=features') : undefined}
      >
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-4 h-4 text-purple-600" />
              <div>
                <span className="text-sm font-medium text-purple-800">Mapped Stages: </span>
                {configuredMqlStages.length > 0 ? (
                  <span className="text-sm text-purple-700">
                    {configuredMqlStages.join(', ')}
                  </span>
                ) : (
                  <span className="text-sm text-purple-500 italic">
                    Using defaults (MQL, marketingqualifiedlead, lead)
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 text-purple-600 text-sm">
                <span>Edit in Settings</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="chart-container">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total MQLs</p>
                    <p className="text-3xl font-bold text-slate-900">{maskNumber(data.total)}</p>
                    <p className="text-xs text-slate-500 mt-1">All who reached MQL</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="chart-container" style={{ borderColor: chartColors[1] + '40', backgroundColor: chartColors[1] + '10' }}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium" style={{ color: chartColors[1] }}>Unworked MQLs</p>
                    <p className="text-3xl font-bold" style={{ color: chartColors[1] }}>{maskNumber(data.unworked_count || 0)}</p>
                    <p className="text-xs mt-1" style={{ color: chartColors[1] }}>Still at MQL stage</p>
                  </div>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: chartColors[1] + '20' }}>
                    <AlertCircle className="w-6 h-6" style={{ color: chartColors[1] }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="chart-container border-green-200 bg-green-50/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-medium">Progressed</p>
                    <p className="text-3xl font-bold text-green-600">{maskNumber(data.progressed_count || 0)}</p>
                    <p className="text-xs text-green-600 mt-1">Moved beyond MQL</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="chart-container">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Progression Rate</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {isPresentationMode ? '~**%' : `${data.total > 0 ? Math.round((data.progressed_count || 0) / data.total * 100) : 0}%`}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">MQL → Next Stage</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Chart */}
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: NAV_DARK_COLOR }}>
                  MQL Trend by Week {selectedRegion !== "all" && `(${selectedRegion.toUpperCase()})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={270}>
                    <BarChart 
                      data={data.trend.map(t => ({ ...t, weekLabel: formatWeekLabel(t.week) }))} 
                      margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} />
                      <YAxis 
                        tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} 
                        tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                      />
                      <Tooltip 
                        contentStyle={{ color: NAV_DARK_COLOR }} 
                        labelFormatter={(label) => `Week of ${label}`}
                        formatter={isPresentationMode ? (value) => [maskNumber(value), 'MQLs'] : undefined}
                      />
                      <Bar dataKey="count" fill={chartColors[0]} name="MQLs" radius={[4, 4, 0, 0]}>
                        {!isPresentationMode && (
                          <LabelList dataKey="count" position="top" fill={NAV_DARK_COLOR} fontSize={11} fontWeight={600} />
                        )}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    No trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage Breakdown */}
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: NAV_DARK_COLOR }}>Current Stage Breakdown</CardTitle>
                <p className="text-sm text-slate-500">Where MQLs are now (sorted by configured stage order)</p>
              </CardHeader>
              <CardContent>
                {data.stage_breakdown && data.stage_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.stage_breakdown} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        type="number" 
                        tick={{ fontSize: 12, fill: NAV_DARK_COLOR }}
                        tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                      />
                      <YAxis 
                        dataKey="stage_label" 
                        type="category" 
                        tick={{ fontSize: 11, fill: NAV_DARK_COLOR }} 
                        width={180}
                      />
                      <Tooltip 
                        contentStyle={{ color: NAV_DARK_COLOR }} 
                        labelFormatter={(value) => value}
                        formatter={isPresentationMode ? (value) => [maskNumber(value), 'Count'] : undefined}
                      />
                      <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]} stroke="#000000" strokeWidth={1}>
                        {data.stage_breakdown.map((entry, index) => {
                          // Use brand colors from chartColors with black strokes
                          return <Cell key={index} fill={chartColors[index % chartColors.length]} stroke="#000000" strokeWidth={1} />;
                        })}
                        {!isPresentationMode && (
                          <LabelList dataKey="count" position="right" fill={NAV_DARK_COLOR} fontSize={11} fontWeight={600} />
                        )}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    No stage data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Regional Comparison - NA vs EMEA */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: NAV_DARK_COLOR }}>Regional Comparison</CardTitle>
              <p className="text-sm text-slate-500">North America vs EMEA</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                {/* NA Summary */}
                <div className="p-4 rounded-lg border" style={{ borderColor: chartColors[0] + '40', backgroundColor: chartColors[0] + '08' }}>
                  <h4 className="font-semibold mb-3" style={{ color: chartColors[0] }}>North America</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total MQLs</span>
                      <span className="font-bold" style={{ color: chartColors[0] }}>{maskNumber(regionalData.na?.total || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Unworked</span>
                      <span className="font-medium text-amber-600">{maskNumber(regionalData.na?.unworked_count || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Progressed</span>
                      <span className="font-medium text-green-600">{maskNumber(regionalData.na?.progressed_count || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-slate-600">Progression Rate</span>
                      <span className="font-bold">
                        {isPresentationMode ? '~**%' : `${regionalData.na?.total > 0 
                          ? Math.round((regionalData.na?.progressed_count || 0) / regionalData.na.total * 100) 
                          : 0}%`}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* EMEA Summary */}
                <div className="p-4 rounded-lg border" style={{ borderColor: chartColors[2] + '40', backgroundColor: chartColors[2] + '08' }}>
                  <h4 className="font-semibold mb-3" style={{ color: chartColors[2] }}>EMEA</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total MQLs</span>
                      <span className="font-bold" style={{ color: chartColors[2] }}>{maskNumber(regionalData.emea?.total || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Unworked</span>
                      <span className="font-medium text-amber-600">{maskNumber(regionalData.emea?.unworked_count || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Progressed</span>
                      <span className="font-medium text-green-600">{maskNumber(regionalData.emea?.progressed_count || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-slate-600">Progression Rate</span>
                      <span className="font-bold">
                        {isPresentationMode ? '~**%' : `${regionalData.emea?.total > 0 
                          ? Math.round((regionalData.emea?.progressed_count || 0) / regionalData.emea.total * 100) 
                          : 0}%`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Side by side bar chart - show placeholder data when masked */}
              <div className="mt-6">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart 
                    data={isPresentationMode ? [
                      { name: 'Total', NA: 100, EMEA: 80 },
                      { name: 'Unworked', NA: 40, EMEA: 30 },
                      { name: 'Progressed', NA: 60, EMEA: 50 }
                    ] : [
                      { name: 'Total', NA: regionalData.na?.total || 0, EMEA: regionalData.emea?.total || 0 },
                      { name: 'Unworked', NA: regionalData.na?.unworked_count || 0, EMEA: regionalData.emea?.unworked_count || 0 },
                      { name: 'Progressed', NA: regionalData.na?.progressed_count || 0, EMEA: regionalData.emea?.progressed_count || 0 }
                    ]}
                    margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} />
                    <YAxis 
                      tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} 
                      tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                    />
                    <Tooltip 
                      contentStyle={{ color: NAV_DARK_COLOR }} 
                      formatter={isPresentationMode ? (value) => [maskNumber(value), ''] : undefined}
                    />
                    <Bar dataKey="NA" name="North America" fill={chartColors[0]} radius={[4, 4, 0, 0]}>
                      {!isPresentationMode && <LabelList dataKey="NA" position="top" fill={NAV_DARK_COLOR} fontSize={11} fontWeight={600} />}
                    </Bar>
                    <Bar dataKey="EMEA" name="EMEA" fill={chartColors[2]} radius={[4, 4, 0, 0]}>
                      {!isPresentationMode && <LabelList dataKey="EMEA" position="top" fill={NAV_DARK_COLOR} fontSize={11} fontWeight={600} />}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Unworked MQLs Section */}
          {data.unworked_mqls && data.unworked_mqls.length > 0 && (
            <Card className="chart-container border-amber-200">
              <CardHeader className="bg-amber-50/50">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-5 h-5" />
                  Unworked MQLs ({isPresentationMode ? '***' : (data.unworked_count || 0)})
                </CardTitle>
                <p className="text-sm text-amber-600 mt-1">
                  These leads are still at MQL stage and have not progressed - they may need attention
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.unworked_mqls.slice(0, 50).map((mql, index) => (
                      <TableRow key={mql.hubspot_id || index} className="bg-amber-50/30">
                        <TableCell className="font-medium">
                          {isPresentationMode 
                            ? maskName(`${mql.first_name || mql.firstname || ''} ${mql.last_name || mql.lastname || ''}`.trim(), index)
                            : `${mql.first_name || mql.firstname || ''} ${mql.last_name || mql.lastname || ''}`.trim() || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-slate-600">
                            <Mail className="w-3 h-3" />
                            {isPresentationMode ? maskEmail(mql.email) : (mql.email || "-")}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            {isPresentationMode ? maskValue(mql.company, 'tenant') : (mql.company || "-")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {mql.analytics_source ? (
                            isPresentationMode ? (
                              <Badge variant="outline" className="text-xs">Source ***</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                {mql.analytics_source === "PAID_SEARCH" ? "Paid Search" : 
                                 mql.analytics_source === "ORGANIC_SEARCH" ? "Organic" :
                                 mql.analytics_source === "DIRECT_TRAFFIC" ? "Direct" :
                                 mql.analytics_source === "REFERRALS" ? "Referral" :
                                 mql.analytics_source || "-"}
                              </Badge>
                            )
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(mql.created_at || mql.createdate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* All MQLs Table */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                All MQLs ({isPresentationMode ? '***' : data.total})
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                All contacts that became MQL during the selected period, including those who have since progressed
              </p>
            </CardHeader>
            <CardContent>
              {data.mqls.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Current Stage</TableHead>
                      <TableHead>MQL Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.mqls.map((mql, index) => {
                      const currentStage = getStageLabel(mql.lifecycle_stage || mql.lifecyclestage);
                      const stageLower = String(mql.lifecycle_stage || mql.lifecyclestage || '').toLowerCase();
                      const isStillMql = stageLower.includes('mql') || stageLower === '1084301189';
                      const isProgressed = !isStillMql;
                      
                      return (
                        <TableRow key={mql.hubspot_id || index} className={isStillMql ? 'bg-amber-50/50' : ''}>
                          <TableCell className="font-medium">
                            {isPresentationMode 
                              ? maskName(`${mql.first_name || mql.firstname || ''} ${mql.last_name || mql.lastname || ''}`.trim(), index)
                              : `${mql.first_name || mql.firstname || ''} ${mql.last_name || mql.lastname || ''}`.trim() || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Mail className="w-3 h-3" />
                              {isPresentationMode ? maskEmail(mql.email) : (mql.email || "-")}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 text-slate-400" />
                              {isPresentationMode ? maskValue(mql.company, 'tenant') : (mql.company || "-")}
                            </div>
                          </TableCell>
                          <TableCell>
                            {mql.analytics_source ? (
                              isPresentationMode ? (
                                <Badge variant="outline" className="text-xs">Source ***</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {mql.analytics_source === "PAID_SEARCH" ? "Paid Search" : 
                                   mql.analytics_source === "ORGANIC_SEARCH" ? "Organic" :
                                   mql.analytics_source === "DIRECT_TRAFFIC" ? "Direct" :
                                   mql.analytics_source === "REFERRALS" ? "Referral" :
                                   mql.analytics_source || "-"}
                                </Badge>
                              )
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={isProgressed 
                                ? "text-green-600 border-green-200 bg-green-50" 
                                : "text-purple-600 border-purple-200"
                              }
                            >
                              {currentStage}
                              {isProgressed && <TrendingUp className="w-3 h-3 ml-1 inline" />}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {formatDate(mql.mql_date || mql.created_at || mql.createdate)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  No MQLs found for the selected period. Try adjusting the date filter or check your HubSpot stage mapping in Settings.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MQLsPage;
